// src/theme/theme.js
import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FF5722',
      light: '#FF8A65',
      dark: '#E64A19',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FFC107',
      light: '#FFD54F',
      dark: '#FF8F00',
      contrastText: '#000000',
    },
    background: {
      default: '#000000',
      paper: 'rgba(255,255,255,0.05)',
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255,255,255,0.7)',
      disabled: 'rgba(255,255,255,0.5)',
    },
    divider: 'rgba(255,255,255,0.1)',
    action: {
      hover: 'rgba(255,255,255,0.08)',
      selected: 'rgba(255,255,255,0.12)',
      disabled: 'rgba(255,255,255,0.3)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      lineHeight: 1.1,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontWeight: 600,
    },
    subtitle2: {
      fontWeight: 600,
      fontSize: '0.9rem',
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
    },
    overline: {
      fontWeight: 600,
      fontSize: '0.9rem',
      letterSpacing: '1.5px',
      textTransform: 'uppercase',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 2px 8px rgba(0,0,0,0.1)',
    '0 4px 12px rgba(0,0,0,0.15)',
    '0 8px 20px rgba(0,0,0,0.15)',
    '0 12px 24px rgba(0,0,0,0.2)',
    '0 16px 32px rgba(0,0,0,0.2)',
    '0 20px 40px rgba(0,0,0,0.25)',
    '0 24px 48px rgba(0,0,0,0.25)',
    '0 28px 56px rgba(0,0,0,0.3)',
    // Custom shadows for primary elements
    '0 10px 20px rgba(255,87,34,0.3)', // index 9 - primary shadow
    '0 15px 30px rgba(255,87,34,0.4)', // index 10 - primary hover shadow
    '0 20px 40px rgba(0,0,0,0.3)',
    '0 24px 48px rgba(0,0,0,0.35)',
    '0 28px 56px rgba(0,0,0,0.4)',
    '0 32px 64px rgba(0,0,0,0.4)',
    '0 36px 72px rgba(0,0,0,0.45)',
    '0 40px 80px rgba(0,0,0,0.45)',
    '0 44px 88px rgba(0,0,0,0.5)',
    '0 48px 96px rgba(0,0,0,0.5)',
    '0 52px 104px rgba(0,0,0,0.55)',
    '0 56px 112px rgba(0,0,0,0.55)',
    '0 60px 120px rgba(0,0,0,0.6)',
    '0 64px 128px rgba(0,0,0,0.6)',
    '0 68px 136px rgba(0,0,0,0.65)',
    '0 72px 144px rgba(0,0,0,0.65)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          padding: '12px 24px',
          fontWeight: 600,
          textTransform: 'none',
          transition: 'all 0.3s ease',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          boxShadow: '0 10px 20px rgba(255,87,34,0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #E64A19, #FF7043)',
            transform: 'translateY(-3px)',
            boxShadow: '0 15px 30px rgba(255,87,34,0.4)',
          },
        },
        outlined: {
          borderColor: 'rgba(255,255,255,0.3)',
          color: '#FFFFFF',
          '&:hover': {
            borderColor: '#FFFFFF',
            backgroundColor: 'rgba(255,255,255,0.05)',
          },
        },
        sizeLarge: {
          padding: '16px 32px',
          fontSize: '1.1rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 16,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-10px)',
            boxShadow: '0 20px 30px rgba(0,0,0,0.2)',
            background: 'rgba(255,255,255,0.08)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontWeight: 700,
          lineHeight: 1.1,
        },
        h2: {
          fontWeight: 700,
        },
        overline: {
          fontWeight: 600,
          fontSize: '0.9rem',
          letterSpacing: '1.5px',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          position: 'relative',
          zIndex: 1,
        },
      },
    },
  },
  // Custom theme extensions
  customColors: {
    gradients: {
      primary: 'linear-gradient(135deg, #FF5722, #FF8A65)',
      secondary: 'linear-gradient(135deg, #FFC107, #FFD54F)',
      background1: 'linear-gradient(to right, #000000, #212121)',
      background2: 'linear-gradient(to right, #000000, #191919)',
      background3: 'linear-gradient(to right, #141414, #212121)',
      overlay: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
    },
    shadows: {
      primary: '0 10px 20px rgba(255,87,34,0.3)',
      primaryHover: '0 15px 30px rgba(255,87,34,0.4)',
    },
    backgrounds: {
      glass: 'rgba(255,255,255,0.05)',
      glassHover: 'rgba(255,255,255,0.08)',
      card: 'rgba(255,255,255,0.02)',
    },
  },
});

export default theme;